import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { 
  ArticlesGrid, 
  ArticleModal, 
  GalleryGrid, 
  GalleryModal, 
  HeroSection, 
  Tabs, 
  CTASection 
} from '@/components';
import { Article, GalleryItem } from '@/types';
import NewArticleModal from '@/components/Modals/NewArticleModal';
import NewGalleryModal from '@/components/Modals/NewGalleryModal';
import './GalleryPage.css';

const GalleryPage = () => {
  // Get data and methods from context
  const { 
    articles = [], 
    galleries = [], 
    loading, 
    error,
    addArticle,
    addGallery,
    uploadFiles,
    refreshData
  } = useAppContext();
  
  // Sort articles by date (newest first)
  const sortedArticles = useMemo(() => {
    if (!Array.isArray(articles)) return [];
    
    return [...articles].sort((a, b) => {
      try {
        // Try to parse dates from either date or createdAt field
        const dateA = a.date || a.createdAt || '';
        const dateB = b.date || b.createdAt || '';
        
        // If dates are already in YYYY-MM-DD format, compare directly
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateA) && /^\d{4}-\d{2}-\d{2}$/.test(dateB)) {
          return dateB.localeCompare(dateA);
        }
        
        // Otherwise, parse as dates
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      } catch (error) {
        console.error('Error sorting articles:', error);
        return 0;
      }
    });
  }, [articles]);

  // UI State
  const [activeTab, setActiveTab] = useState<'articles' | 'gallery'>('articles');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [selectedGallery, setSelectedGallery] = useState<GalleryItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  
  // Refs
  const rotationInterval = useRef<NodeJS.Timeout | null>(null);

  // Refresh data when component mounts
  useEffect(() => {
    refreshData();
    // We don't need to include refreshData in the dependency array
    // because it's a stable function from the context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as 'articles' | 'gallery');
  };

  // Open article
  const openArticle = (article: Article) => {
    setSelectedArticle(article);
  };

  // Open gallery
  const openGallery = (gallery: GalleryItem) => {
    setSelectedGallery(gallery);
    setCurrentImageIndex(0);
  };

  // Close modal and cleanup
  const closeModal = useCallback(() => {
    setSelectedArticle(null);
    setSelectedGallery(null);
    setCurrentImageIndex(0);
    setIsAutoRotating(false);
  }, []);

  // Handle next/previous image in gallery
  const nextImage = useCallback(() => {
    if (!selectedGallery) return;
    setCurrentImageIndex(prev => 
      prev === selectedGallery.images.length - 1 ? 0 : prev + 1
    );
  }, [selectedGallery]);

  const prevImage = useCallback(() => {
    if (!selectedGallery) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? selectedGallery.images.length - 1 : prev - 1
    );
  }, [selectedGallery]);

  // Auto-rotation effect
  useEffect(() => {
    if (isAutoRotating && selectedGallery) {
      rotationInterval.current = setInterval(nextImage, 3000);
      return () => {
        if (rotationInterval.current) {
          clearInterval(rotationInterval.current);
        }
      };
    }
  }, [isAutoRotating, selectedGallery, nextImage]);

  const toggleAutoRotate = useCallback(() => {
    setIsAutoRotating(prev => !prev);
  }, []);

  // Handle file upload using context
  const handleFileUpload = async (files: File[]) => {
    try {
      setUploading(true);
      const uploadedFiles = await uploadFiles(files);
      return uploadedFiles.map(file => file.url);
    } catch (err) {
      console.error('Error uploading files:', err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Handle article submission
  const handleArticleSubmit = async (articleData: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setIsSubmitting(true);
      await addArticle(articleData);
      setShowArticleModal(false);
      // Refresh data to show the new article
      await refreshData();
    } catch (err) {
      console.error('Error submitting article:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle gallery submission
  const handleGallerySubmit = async (galleryData: Omit<GalleryItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setIsSubmitting(true);
      await addGallery(galleryData);
      setShowGalleryModal(false);
      // Refresh data to show the new gallery
      await refreshData();
    } catch (err) {
      console.error('Error submitting gallery:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tabs configuration
  const tabs = [
    { id: 'articles', label: 'Articles' },
    { id: 'gallery', label: 'Gallery' }
  ];

  // Check if user is admin
  const isAdmin = localStorage.getItem('adminToken') !== null;

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <HeroSection 
        title="Our Gallery & News"
        subtitle="Explore our collection of community moments, events, and stories from Kayamandi"
        className="gallery-hero"
      />

      {/* Tab Navigation */}
      <Tabs 
        activeTab={activeTab}
        tabs={tabs}
        onTabChange={handleTabChange}
        className="tabs-section"
      />

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          {loading ? (
            <div className="loading-spinner">Loading...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : activeTab === 'articles' ? (
            <>
              <ArticlesGrid 
                articles={sortedArticles}
                onArticleClick={openArticle}
              />
            </>
          ) : (
            <>
              <GalleryGrid 
                galleries={galleries}
                onGalleryClick={openGallery}
              />
            </>
          )}
        </div>
      </main>

      {/* CTA Section */}
      <CTASection
        title="Become Part of Our Story"
        description="Join us in making a difference in the Kayamandi community. Your support helps us create more moments worth capturing."
        primaryButton={{
          text: 'Donate Now',
          to: '/donate'
        }}
        secondaryButton={{
          text: 'Volunteer',
          to: '/volunteer'
        }}
        className="cta-section"
      />

      {/* New Article Modal */}
      <NewArticleModal
        isOpen={showArticleModal}
        onClose={() => setShowArticleModal(false)}
        onSubmit={handleArticleSubmit}
        isSubmitting={isSubmitting}
      />

      {/* New Gallery Modal */}
      <NewGalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        onSubmit={handleGallerySubmit}
        isSubmitting={isSubmitting}
      />

      {/* Article Modal */}
      <ArticleModal
        article={selectedArticle}
        onClose={closeModal}
      />

      {/* Gallery Modal */}
      <GalleryModal
        gallery={selectedGallery}
        currentImageIndex={currentImageIndex}
        isAutoRotating={isAutoRotating}
        onClose={closeModal}
        onNext={nextImage}
        onPrev={prevImage}
        onToggleAutoRotate={toggleAutoRotate}
      />
    </div>
  );
};

export default GalleryPage;
